import React, { useState, useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { TemplateType, TemplateConfig } from './types';
import { isInRegistry, renderRegistryPreview } from './templateRegistry/adapters';
import {
  getGradient,
  getPadding,
  getSpacing,
  getRadius,
  getGap,
  getAspectHeight,
  getCardWidth,
  getCarouselSlideWidth,
  getFontSize,
  getImageSize,
  getLinkDecoration,
  getContainerBackground,
  getCardShadow,
  getSidebarContainerStyle,
  getHeadingStyle,
  getInlineCalloutStyle,
  getWidthStyle,
  getFloatStyle,
  getContextualBorderStyle,
  getCardBackground,
  getColumnDividerStyle,
  generateLinks,
  SAMPLE_LINKS,
  SAMPLE_PRODUCTS,
  SAMPLE_ARTICLES,
  COLUMN_HEADINGS,
  SIDEBAR_TITLES,
  COLORS,
} from './previewHelpers';

const useStyles = makeStyles((theme) => ({
  previewContainer: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  link: {
    color: COLORS.primaryDark,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  heading: {
    color: COLORS.navy,
    marginBottom: '0.75rem',
    fontWeight: 600,
  },
  gradientBox: {
    flexShrink: 0,
  },
  carouselTrack: {
    display: 'flex',
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      height: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#cbd5e1',
      borderRadius: 3,
    },
  },
}));

interface TemplatePreviewRendererProps {
  templateId: TemplateType;
  config: TemplateConfig;
  isGalleryPreview?: boolean;
}

// Gradient Box Component
const GradientBox: React.FC<{
  index: number;
  width?: string;
  height?: string;
  borderRadius?: string;
}> = ({ index, width = '40px', height = '40px', borderRadius = '4px' }) => (
  <div
    style={{
      width,
      height,
      background: getGradient(index),
      borderRadius,
      flexShrink: 0,
    }}
  />
);

// Main Preview Renderer
const TemplatePreviewRenderer: React.FC<TemplatePreviewRendererProps> = ({
  templateId,
  config,
  isGalleryPreview = false,
}) => {
  const classes = useStyles();
  const c = config;

  // For carousel scrolling
  const carouselRef = useRef<HTMLDivElement>(null);
  const [expandedLists, setExpandedLists] = useState<Record<string, boolean>>({});

  const toggleExpand = (listId: string) => {
    setExpandedLists((prev) => ({ ...prev, [listId]: !prev[listId] }));
  };

  const scrollCarousel = (direction: number) => {
    if (carouselRef.current) {
      const slideWidth = getCarouselSlideWidth(c.cardSize as string) + 16;
      carouselRef.current.scrollBy({ left: direction * slideWidth, behavior: 'smooth' });
    }
  };

  // Scale factor for gallery previews
  const scale = isGalleryPreview ? 0.6 : 1;
  const scaledFontSize = (size: string) => {
    const num = parseFloat(size);
    return `${num * scale}rem`;
  };

  // Check if template is in new registry first (plug-and-play templates)
  if (isInRegistry(templateId)) {
    const registryPreview = renderRegistryPreview(templateId, config, isGalleryPreview, expandedLists, toggleExpand);
    if (registryPreview) return <>{registryPreview}</>;
  }

  // Legacy templates - use switch statement
  switch (templateId) {
    case 'vertical-link-list': {
      const linkCount = Math.min((c.maxLinks as number) || 8, 8);
      const initialVisible = c.showViewMore ? Math.min(4, linkCount) : linkCount;
      const isExpanded = expandedLists['vertical-list'];
      const visibleCount = isExpanded ? linkCount : initialVisible;
      const listStyleType = c.listStyle === 'bullets' ? 'disc' : c.listStyle === 'numbered' ? 'decimal' : 'none';
      const HeadingTag = (c.headingTag as 'h2' | 'h3' | 'h4' | 'h5') || 'h3';

      return (
        <Box
          style={{ padding: getPadding(c.containerPadding as string) }}
          className={(c.customCssClass as string) || undefined}
        >
          <Typography
            variant={isGalleryPreview ? 'subtitle2' : 'h6'}
            component={HeadingTag}
            className={classes.heading}
            style={{ fontSize: isGalleryPreview ? '0.8rem' : undefined }}
          >
            {c.moduleHeading as string}
          </Typography>
          <Box
            component={c.listStyle === 'numbered' ? 'ol' : 'ul'}
            style={{
              listStyleType,
              paddingLeft: listStyleType !== 'none' ? '1.5rem' : 0,
              margin: 0,
            }}
          >
            {generateLinks(linkCount)
              .slice(0, visibleCount)
              .map((text, i) => (
                <li key={i} style={{ padding: `${getSpacing(c.itemSpacing as string)} 0` }}>
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    style={{
                      ...getLinkDecoration(c.linkStyle as string),
                      color: COLORS.primaryDark,
                      fontSize: isGalleryPreview ? '0.7rem' : '0.9rem',
                    }}
                  >
                    {text}
                  </a>
                </li>
              ))}
          </Box>
          {c.showViewMore && linkCount > initialVisible && (
            <Box mt={1}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  toggleExpand('vertical-list');
                }}
                style={{
                  color: COLORS.textMuted,
                  fontSize: isGalleryPreview ? '0.65rem' : '0.85rem',
                  textDecoration: 'none',
                }}
              >
                {isExpanded ? '← Show Less' : `${c.viewMoreText} →`}
              </a>
            </Box>
          )}
        </Box>
      );
    }

    case 'multi-column-links': {
      const cols = parseInt(c.columns as string) || 3;
      const linksPerCol = Math.min((c.maxLinksPerModule as number) || 5, 6);
      const initialColLinks = c.showViewMore ? Math.min(3, linksPerCol) : linksPerCol;
      // responsiveBehavior: 'stack' | 'scroll' | '2-col-then-stack' - used for mobile CSS
      const responsiveClass =
        c.responsiveBehavior === 'scroll'
          ? 'responsive-scroll'
          : c.responsiveBehavior === '2-col-then-stack'
          ? 'responsive-2col'
          : '';

      return (
        <Box className={`${c.customCssClass || ''} ${responsiveClass}`.trim() || undefined}>
          {c.sectionHeading && (
            <Typography variant="h6" className={classes.heading}>
              {c.sectionHeading as string}
            </Typography>
          )}
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(cols, isGalleryPreview ? 3 : cols)}, 1fr)`,
              gap: '0.75rem',
              ...getContainerBackground(c.containerBackground as string),
              padding: c.containerBackground !== 'none' ? '1rem' : 0,
              borderRadius: '8px',
            }}
          >
            {COLUMN_HEADINGS.slice(0, Math.min(cols, isGalleryPreview ? 3 : cols)).map((heading, colIdx) => {
              const colExpanded = expandedLists[`col-${colIdx}`];
              const visibleLinks = colExpanded ? linksPerCol : initialColLinks;

              return (
                <Box key={colIdx} style={getColumnDividerStyle(c.columnDivider as string, colIdx)}>
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 600,
                      color: COLORS.navy,
                      fontSize: isGalleryPreview ? '0.65rem' : '0.85rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    {heading}
                  </Typography>
                  <Box component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {generateLinks(linksPerCol)
                      .slice(0, visibleLinks)
                      .map((_, j) => (
                        <li key={j} style={{ padding: '0.25rem 0' }}>
                          <a
                            href="#"
                            onClick={(e) => e.preventDefault()}
                            style={{
                              color: COLORS.primaryDark,
                              fontSize: isGalleryPreview ? '0.6rem' : '0.8rem',
                              textDecoration: 'none',
                            }}
                          >
                            Sample Link {j + 1}
                          </a>
                        </li>
                      ))}
                  </Box>
                  {c.showViewMore && linksPerCol > initialColLinks && (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleExpand(`col-${colIdx}`);
                      }}
                      style={{
                        color: COLORS.textMuted,
                        fontSize: isGalleryPreview ? '0.55rem' : '0.75rem',
                        textDecoration: 'none',
                        display: 'block',
                        marginTop: '0.5rem',
                      }}
                    >
                      {colExpanded ? 'Show Less' : 'View More'}
                    </a>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }

    case 'image-card-grid': {
      const gridCols = c.gridColumns === 'auto-fit' ? 3 : parseInt(c.gridColumns as string);
      const displayCols = Math.min(gridCols, isGalleryPreview ? 3 : 4);
      const cardCount = Math.min((c.maxCards as number) || 8, displayCols);
      const cardSize = (c.cardSize as string) || 'medium';
      const imgHeight = getAspectHeight(c.imageAspectRatio as string, cardSize);
      const fontSize = getFontSize(cardSize);
      const isOverlay = c.titlePosition === 'overlay-bottom' || c.titlePosition === 'overlay-center';
      const HeadingTag = (c.headingTag as 'h2' | 'h3' | 'h4') || 'h2';

      // Hover effect styles
      const getHoverStyles = (): React.CSSProperties => {
        switch (c.hoverEffect) {
          case 'lift':
            return { transition: 'transform 0.2s, box-shadow 0.2s' };
          case 'zoom':
            return { transition: 'transform 0.2s', overflow: 'hidden' };
          case 'darken':
            return { transition: 'filter 0.2s' };
          default:
            return {};
        }
      };

      const getTitleContainerStyle = (): React.CSSProperties => {
        if (c.titlePosition === 'overlay-bottom') {
          return {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
            padding: '0.75rem',
          };
        }
        if (c.titlePosition === 'overlay-center') {
          return {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0,0,0,0.7)',
            padding: '0.5rem 0.75rem',
            borderRadius: '4px',
          };
        }
        return { padding: '0.75rem' };
      };

      return (
        <Box className={(c.customCssClass as string) || undefined}>
          <Typography
            variant={isGalleryPreview ? 'subtitle2' : 'h6'}
            component={HeadingTag}
            className={classes.heading}
            style={{ fontSize: isGalleryPreview ? '0.8rem' : undefined }}
          >
            {c.moduleHeading as string}
          </Typography>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${displayCols}, 1fr)`,
              gap: isGalleryPreview ? '0.5rem' : '1rem',
            }}
          >
            {SAMPLE_PRODUCTS.slice(0, cardCount).map((title, i) => (
              <Box
                key={i}
                style={{
                  borderRadius: getRadius(c.cardBorderRadius as string),
                  overflow: 'hidden',
                  ...getCardShadow(c.cardStyle as string),
                  ...getHoverStyles(),
                  background: 'white',
                  position: isOverlay ? 'relative' : undefined,
                  cursor: c.hoverEffect !== 'none' ? 'pointer' : undefined,
                }}
                onMouseEnter={(e) => {
                  if (c.hoverEffect === 'lift') {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  } else if (c.hoverEffect === 'zoom') {
                    const img = e.currentTarget.querySelector('div') as HTMLElement;
                    if (img) img.style.transform = 'scale(1.05)';
                  } else if (c.hoverEffect === 'darken') {
                    e.currentTarget.style.filter = 'brightness(0.9)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (c.hoverEffect === 'lift') {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  } else if (c.hoverEffect === 'zoom') {
                    const img = e.currentTarget.querySelector('div') as HTMLElement;
                    if (img) img.style.transform = '';
                  } else if (c.hoverEffect === 'darken') {
                    e.currentTarget.style.filter = '';
                  }
                }}
              >
                <div
                  style={{
                    height: isGalleryPreview ? imgHeight * 0.5 : imgHeight,
                    background: getGradient(i),
                  }}
                />
                {c.showTitle && (
                  <div style={getTitleContainerStyle()}>
                    <div
                      style={{
                        fontSize: isGalleryPreview ? '0.65rem' : fontSize,
                        fontWeight: 500,
                        color: isOverlay ? 'white' : COLORS.navy,
                      }}
                    >
                      {title}
                    </div>
                    {c.showDescription && (
                      <div
                        style={{
                          fontSize: isGalleryPreview ? '0.55rem' : `calc(${fontSize} - 0.15rem)`,
                          color: isOverlay ? 'rgba(255,255,255,0.8)' : COLORS.textMuted,
                          marginTop: '0.25rem',
                        }}
                      >
                        Sample description text
                      </div>
                    )}
                  </div>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    case 'rich-article-cards': {
      const artCols = parseInt(c.gridColumns as string) || 3;
      const artDisplayCols = Math.min(artCols, isGalleryPreview ? 2 : 3);
      const artCardCount = Math.min((c.maxCards as number) || 6, artDisplayCols);
      const cardSize = (c.cardSize as string) || 'medium';
      const artImgHeight = getAspectHeight(c.imageAspectRatio as string, cardSize);
      const fontSize = getFontSize(cardSize);
      const isScrollLayout = c.layout === 'carousel' || c.layout === 'horizontal-scroll';

      return (
        <Box className={(c.customCssClass as string) || undefined}>
          <Typography
            variant={isGalleryPreview ? 'subtitle2' : 'h6'}
            className={classes.heading}
            style={{ fontSize: isGalleryPreview ? '0.8rem' : undefined }}
          >
            {c.moduleHeading as string}
          </Typography>
          <Box
            style={
              isScrollLayout
                ? { display: 'flex', overflowX: 'auto', gap: '1rem', paddingBottom: '0.5rem' }
                : { display: 'grid', gridTemplateColumns: `repeat(${artDisplayCols}, 1fr)`, gap: '1rem' }
            }
          >
            {SAMPLE_ARTICLES.slice(0, artCardCount).map((art, i) => (
              <Box
                key={i}
                style={{
                  ...(isScrollLayout ? { minWidth: getCardWidth(cardSize), flexShrink: 0 } : {}),
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {c.showImage && (
                  <div
                    style={{
                      height: isGalleryPreview ? artImgHeight * 0.5 : artImgHeight,
                      background: getGradient(i + 2),
                    }}
                  />
                )}
                <Box p={isGalleryPreview ? 0.5 : 1.5}>
                  {c.showCategory && (
                    <span
                      style={{
                        fontSize: isGalleryPreview ? '0.5rem' : '0.65rem',
                        background: 'rgba(62,207,142,0.15)',
                        color: COLORS.primaryDark,
                        padding: '0.15rem 0.4rem',
                        borderRadius: '3px',
                        display: 'inline-block',
                        marginBottom: '0.35rem',
                        fontWeight: 600,
                      }}
                    >
                      {art.cat}
                    </span>
                  )}
                  <div
                    style={{
                      fontSize: isGalleryPreview ? '0.6rem' : fontSize,
                      fontWeight: 500,
                      color: COLORS.navy,
                      marginBottom: '0.25rem',
                    }}
                  >
                    {art.title}
                  </div>
                  <div
                    style={{
                      fontSize: isGalleryPreview ? '0.5rem' : `calc(${fontSize} - 0.15rem)`,
                      color: '#94a3b8',
                    }}
                  >
                    {c.showDate && art.date}
                    {c.showDate && c.showReadingTime && ' · '}
                    {c.showReadingTime && `${art.time} min`}
                  </div>
                  {c.showExcerpt && (
                    <div
                      style={{
                        fontSize: isGalleryPreview ? '0.5rem' : `calc(${fontSize} - 0.1rem)`,
                        color: '#64748b',
                        marginTop: '0.35rem',
                        lineHeight: 1.4,
                      }}
                    >
                      This is a sample excerpt showing what the description would look like...
                    </div>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    case 'image-carousel': {
      const visibleSlides = parseInt(c.visibleSlides as string) || 4;
      const cardSize = (c.cardSize as string) || 'medium';
      const slideCount = Math.min(8, visibleSlides + 3);
      const slideWidth = getCarouselSlideWidth(cardSize);
      const carouselImgHeight = getAspectHeight(c.imageAspectRatio as string, cardSize);
      const fontSize = getFontSize(cardSize);

      return (
        <Box className={(c.customCssClass as string) || undefined}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography
              variant={isGalleryPreview ? 'subtitle2' : 'h6'}
              className={classes.heading}
              style={{ marginBottom: 0, fontSize: isGalleryPreview ? '0.8rem' : undefined }}
            >
              {c.moduleHeading as string}
            </Typography>
            {c.showArrows && !isGalleryPreview && (
              <Box display="flex" style={{ gap: '0.35rem' }}>
                <IconButton
                  size="small"
                  onClick={() => scrollCarousel(-1)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px' }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => scrollCarousel(1)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px' }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          <div ref={carouselRef} className={classes.carouselTrack} style={{ gap: getGap(c.gap as string) }}>
            {SAMPLE_PRODUCTS.slice(0, slideCount).map((name, i) => (
              <div
                key={i}
                style={{
                  minWidth: isGalleryPreview ? slideWidth * 0.5 : slideWidth,
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    height: isGalleryPreview ? carouselImgHeight * 0.5 : carouselImgHeight,
                    background: getGradient(i),
                  }}
                />
                {c.cardStyle !== 'image-only' && (
                  <div
                    style={{
                      padding: '0.5rem',
                      fontSize: isGalleryPreview ? '0.55rem' : fontSize,
                      textAlign: 'center',
                      color: COLORS.navy,
                      fontWeight: 500,
                    }}
                  >
                    {name}
                  </div>
                )}
                {c.cardStyle === 'image-title-meta' && (
                  <div
                    style={{
                      padding: '0 0.5rem 0.5rem',
                      fontSize: isGalleryPreview ? '0.5rem' : `calc(${fontSize} - 0.1rem)`,
                      textAlign: 'center',
                      color: COLORS.textMuted,
                    }}
                  >
                    $29.99
                  </div>
                )}
              </div>
            ))}
          </div>
          {c.showDots && !isGalleryPreview && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.5rem',
                marginTop: '0.75rem',
              }}
            >
              {Array.from({ length: Math.ceil(slideCount / visibleSlides) }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: i === 0 ? COLORS.primary : '#cbd5e1',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Box>
          )}
          {c.autoPlay && !isGalleryPreview && (
            <Typography
              variant="caption"
              style={{
                display: 'block',
                textAlign: 'center',
                marginTop: '0.35rem',
                fontStyle: 'italic',
                color: COLORS.textMuted,
              }}
            >
              ▶ Auto-play active (3s interval)
            </Typography>
          )}
        </Box>
      );
    }

    case 'sidebar-link-box': {
      const totalLinks = Math.min((c.maxLinks as number) || 5, 8);
      const sidebarVisibleLinks = c.expandable ? Math.min((c.initialVisible as number) || 5, totalLinks) : totalLinks;
      const isExpanded = expandedLists['sidebar-links'];
      const visibleCount = isExpanded ? totalLinks : sidebarVisibleLinks;
      const hiddenCount = totalLinks - sidebarVisibleLinks;

      const getLinkPrefix = (index: number) => {
        if (c.linkStyle === 'numbered') {
          return <span style={{ color: COLORS.primary, fontWeight: 600, marginRight: '0.35rem' }}>{index + 1}.</span>;
        }
        if (c.linkStyle === 'icon-prefix') {
          return <span style={{ color: COLORS.primary, marginRight: '0.35rem' }}>→</span>;
        }
        return null;
      };

      return (
        <Box
          className={(c.customCssClass as string) || undefined}
          style={{
            ...getSidebarContainerStyle(c.containerStyle as string),
            maxWidth: isGalleryPreview ? '100%' : '220px',
            padding: '1rem',
            borderRadius: '8px',
          }}
        >
          <Typography
            variant="subtitle2"
            style={{
              ...getHeadingStyle(c.headingStyle as string),
              color: COLORS.navy,
              marginBottom: '0.75rem',
              fontSize: isGalleryPreview ? '0.7rem' : '0.85rem',
            }}
          >
            {c.moduleHeading as string}
          </Typography>
          <Box component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {generateLinks(totalLinks)
              .slice(0, visibleCount)
              .map((_, i) => (
                <li
                  key={i}
                  style={{
                    borderBottom: c.showDividers ? '1px solid #e2e8f0' : undefined,
                    padding: '0.4rem 0',
                  }}
                >
                  {getLinkPrefix(i)}
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    style={{
                      color: COLORS.primaryDark,
                      fontSize: isGalleryPreview ? '0.6rem' : '0.8rem',
                      textDecoration: 'none',
                    }}
                  >
                    Sample Link Item {i + 1}
                  </a>
                </li>
              ))}
          </Box>
          {c.expandable && hiddenCount > 0 && !isGalleryPreview && (
            <Button
              fullWidth
              size="small"
              onClick={() => toggleExpand('sidebar-links')}
              style={{
                marginTop: '0.5rem',
                fontSize: '0.7rem',
                textTransform: 'none',
                border: '1px solid #e2e8f0',
              }}
            >
              {isExpanded ? 'Show Less ▲' : `Show ${hiddenCount} More ▼`}
            </Button>
          )}
        </Box>
      );
    }

    case 'sidebar-card-stack': {
      const stackCardCount = Math.min((c.maxCards as number) || 4, 4);
      const imgSize = getImageSize(c.imageSize as string);
      const cardBg = getCardBackground(c.cardBackground as string);

      return (
        <Box
          className={(c.customCssClass as string) || undefined}
          style={{
            maxWidth: isGalleryPreview ? '100%' : '220px',
            padding: '1rem',
          }}
        >
          <Typography
            variant="subtitle2"
            style={{
              fontWeight: 600,
              color: COLORS.navy,
              marginBottom: '0.75rem',
              fontSize: isGalleryPreview ? '0.7rem' : '0.85rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {c.moduleHeading as string}
          </Typography>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {SIDEBAR_TITLES.slice(0, stackCardCount).map((title, i) => (
              <Box
                key={i}
                style={{
                  display: 'flex',
                  gap: '0.6rem',
                  alignItems: c.imagePosition === 'top' ? 'flex-start' : 'center',
                  flexDirection: c.imagePosition === 'top' ? 'column' : 'row',
                  background: cardBg,
                  padding: '0.6rem',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                }}
              >
                {c.showImage && (
                  <GradientBox
                    index={i}
                    width={isGalleryPreview ? '30px' : imgSize}
                    height={isGalleryPreview ? '30px' : imgSize}
                    borderRadius="6px"
                  />
                )}
                <div>
                  {c.showCategory && (
                    <div
                      style={{
                        fontSize: isGalleryPreview ? '0.45rem' : '0.55rem',
                        color: COLORS.primaryDark,
                        fontWeight: 600,
                        marginBottom: '0.15rem',
                      }}
                    >
                      CATEGORY
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: isGalleryPreview ? '0.55rem' : '0.75rem',
                      color: COLORS.navy,
                      lineHeight: 1.3,
                    }}
                  >
                    {title}
                  </div>
                </div>
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    case 'inline-reading-callout': {
      const inlineLinkCount = Math.min((c.maxLinks as number) || 3, 5);
      const isInlineDisplay = c.linkDisplay !== 'list';
      const linkSeparator = c.linkDisplay === 'inline-comma' ? ',' : c.linkDisplay === 'inline-bullet' ? ' •' : '';

      return (
        <Box
          className={(c.customCssClass as string) || undefined}
          style={{
            ...getInlineCalloutStyle(c.style as string),
            ...getFloatStyle(c.floatPosition as string),
            width: getWidthStyle(c.width as string),
            padding: '1rem',
          }}
        >
          <Typography
            variant="subtitle2"
            style={{
              fontWeight: 600,
              color: COLORS.primaryDark,
              marginBottom: '0.5rem',
              fontSize: isGalleryPreview ? '0.7rem' : '0.85rem',
            }}
          >
            {c.showIcon && '📚 '}
            {c.calloutHeading as string}
          </Typography>
          <Box
            component="ul"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              ...(isInlineDisplay ? { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' } : {}),
            }}
          >
            {generateLinks(inlineLinkCount).map((_, i) => (
              <li key={i} style={{ padding: '0.2rem 0' }}>
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  style={{
                    color: COLORS.primaryDark,
                    fontSize: isGalleryPreview ? '0.6rem' : '0.8rem',
                    textDecoration: 'none',
                  }}
                >
                  Related Article {i + 1}
                </a>
                {isInlineDisplay && i < inlineLinkCount - 1 && linkSeparator}
              </li>
            ))}
          </Box>
        </Box>
      );
    }

    case 'contextual-link-box': {
      const getFlexDirection = (): React.CSSProperties['flexDirection'] => {
        if (c.imagePosition === 'left') return 'row-reverse';
        if (c.imagePosition === 'top') return 'column';
        return 'row';
      };

      const ctaStyles: React.CSSProperties =
        c.ctaStyle === 'button'
          ? {
              display: 'inline-block',
              background: COLORS.primary,
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: isGalleryPreview ? '0.6rem' : '0.8rem',
              fontWeight: 500,
            }
          : {
              color: COLORS.primaryDark,
              fontWeight: 600,
              fontSize: isGalleryPreview ? '0.6rem' : '0.8rem',
              textDecoration: 'none',
            };

      const ctaAlignStyle: React.CSSProperties =
        c.ctaPosition === 'right'
          ? { textAlign: 'right' as const }
          : c.ctaPosition === 'below'
          ? { marginTop: '0.5rem' }
          : {};

      const headingSrc = (c.headingSource as string) || 'anchorText';
      const headingText =
        headingSrc === 'custom'
          ? (c.heading as string)
          : headingSrc === 'title'
          ? 'Advanced Linking Strategies Guide'
          : headingSrc === 'h1'
          ? 'Best Practices for Internal Linking'
          : 'internal linking strategies';

      const maxBoxes = parseInt(c.maxBoxes as string) || 6;
      const initialVisible = parseInt(c.initialVisible as string) || 3;
      const previewCount = isGalleryPreview ? 1 : Math.min(c.expandable ? initialVisible : maxBoxes, 3);

      return (
        <div>
          {Array.from({ length: previewCount }).map((_, idx) => (
            <Box
              key={idx}
              className={(c.customCssClass as string) || undefined}
              style={{
                ...getContextualBorderStyle(c.borderStyle as string),
                borderRadius: '10px',
                padding: isGalleryPreview ? '0.75rem' : '1.25rem',
                display: 'flex',
                gap: isGalleryPreview ? '0.75rem' : '1.25rem',
                alignItems: 'center',
                flexDirection: getFlexDirection(),
                marginBottom: idx < previewCount - 1 ? '0.75rem' : 0,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: isGalleryPreview ? '0.75rem' : '1rem',
                    fontWeight: 600,
                    color: COLORS.navy,
                    marginBottom: '0.35rem',
                  }}
                >
                  {c.showIcon && '📖 '}
                  {headingText}
                </div>
                {c.showDescription && (
                  <div
                    style={{
                      fontSize: isGalleryPreview ? '0.6rem' : '0.85rem',
                      color: COLORS.textMuted,
                      marginBottom: '0.75rem',
                      lineHeight: 1.5,
                    }}
                  >
                    Learn more about advanced linking strategies and best practices.
                  </div>
                )}
                <div style={ctaAlignStyle}>
                  <a href="#" onClick={(e) => e.preventDefault()} style={ctaStyles}>
                    {c.ctaText as string} →
                  </a>
                </div>
              </div>
              {c.showImage && (
                <GradientBox
                  index={idx}
                  width={isGalleryPreview ? '50px' : '80px'}
                  height={isGalleryPreview ? '50px' : '80px'}
                  borderRadius="8px"
                />
              )}
            </Box>
          ))}
          {c.expandable && !isGalleryPreview && (
            <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                style={{
                  color: COLORS.primary,
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                }}
              >
                {(c.viewMoreText as string) || 'Show More'} ({maxBoxes - initialVisible} more)
              </a>
            </div>
          )}
        </div>
      );
    }

    case 'related-searches': {
      const SAMPLE_SEARCHES = [
        'mens suits',
        'mens black suits',
        'mens khakis',
        'mens dress shirts',
        'mens blazers',
        'mens ties',
        'formal wear',
        'business casual',
        'slim fit suits',
        'wedding suits',
      ];
      const maxPills = Math.min((c.maxPills as number) || 10, 10);
      const pillsPerRow = (c.pillsPerRow as number) || 5;
      const isExpandable = Boolean(c.expandable);
      const viewMoreText = (c.viewMoreText as string) || 'Show More';
      const pillColor = (c.pillColor as string) || '#2563eb';
      const textColor = (c.textColor as string) || '#ffffff';
      const backgroundColor = (c.backgroundColor as string) || '#f8fafc';
      const hoverStyle = (c.hoverStyle as string) || 'darken';

      // Expandable state
      const isExpanded = expandedLists['related-searches'];
      const visiblePills = isExpanded || !isExpandable ? maxPills : Math.min(pillsPerRow, maxPills);
      const hasMorePills = maxPills > pillsPerRow;

      // Size mappings
      const sizeMap: Record<string, { padding: string; fontSize: string; iconSize: number }> = {
        small: { padding: '6px 12px', fontSize: isGalleryPreview ? '0.5rem' : '0.75rem', iconSize: 12 },
        medium: { padding: '8px 16px', fontSize: isGalleryPreview ? '0.6rem' : '0.8rem', iconSize: 14 },
        large: { padding: '12px 20px', fontSize: isGalleryPreview ? '0.7rem' : '0.9rem', iconSize: 16 },
      };

      const radiusMap: Record<string, number> = {
        square: 4,
        rounded: 8,
        pill: 50,
      };

      const size = sizeMap[(c.pillSize as string) || 'medium'] || sizeMap.medium;
      const radius = radiusMap[(c.borderRadius as string) || 'pill'] || radiusMap.pill;

      // Helper to lighten/darken colors
      const adjustColor = (color: string, amount: number): string => {
        const hex = color.replace('#', '');
        const num = parseInt(hex, 16);
        const r = Math.min(255, Math.max(0, (num >> 16) + amount));
        const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
        const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
      };

      // Get hover styles based on hoverStyle option
      const getHoverStyles = (baseStyles: React.CSSProperties): React.CSSProperties => {
        switch (hoverStyle) {
          case 'darken':
            if (c.pillStyle === 'filled') {
              return { ...baseStyles, backgroundColor: adjustColor(pillColor, -30) };
            }
            return { ...baseStyles, backgroundColor: adjustColor(pillColor, -30), color: textColor };
          case 'lighten':
            if (c.pillStyle === 'filled') {
              return { ...baseStyles, backgroundColor: adjustColor(pillColor, 30) };
            }
            return { ...baseStyles, backgroundColor: `${pillColor}15` };
          case 'invert':
            if (c.pillStyle === 'filled') {
              return {
                ...baseStyles,
                backgroundColor: 'transparent',
                color: pillColor,
                border: `1.5px solid ${pillColor}`,
              };
            }
            return { ...baseStyles, backgroundColor: pillColor, color: textColor, border: 'none' };
          default:
            return baseStyles;
        }
      };

      // Pill styles based on pillStyle
      const getPillStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
          padding: size.padding,
          fontSize: size.fontSize,
          borderRadius: radius,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          textDecoration: 'none',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        };

        switch (c.pillStyle) {
          case 'filled':
            return { ...base, backgroundColor: pillColor, color: textColor, border: 'none' };
          case 'ghost':
            return { ...base, backgroundColor: 'transparent', color: pillColor, border: 'none' };
          case 'outlined':
          default:
            return { ...base, backgroundColor: 'transparent', color: pillColor, border: `1.5px solid ${pillColor}` };
        }
      };

      // Container styles based on layout
      const getContainerStyles = (): React.CSSProperties => {
        switch (c.layout) {
          case 'scroll':
            return { display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' };
          case 'grid':
            return { display: 'grid', gridTemplateColumns: `repeat(${c.columns || 3}, 1fr)`, gap: '8px' };
          default:
            return { display: 'flex', flexWrap: 'wrap', gap: '8px' };
        }
      };

      // Heading styles
      const getHeadingStyles = (): React.CSSProperties => {
        switch (c.headingStyle) {
          case 'minimal':
            return {
              margin: 0,
              fontSize: isGalleryPreview ? '0.6rem' : '0.8rem',
              fontWeight: 500,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            };
          case 'simple':
          case 'with-icon':
          default:
            return {
              margin: 0,
              fontSize: isGalleryPreview ? '0.7rem' : '0.9rem',
              fontWeight: 600,
              color: COLORS.navy,
            };
        }
      };

      const SearchIconSvg = ({ size: iconSize }: { size: number }) => (
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{ flexShrink: 0 }}
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      );

      // Pill component with hover handling
      const Pill: React.FC<{ term: string; index: number }> = ({ term, index }) => {
        const [isHovered, setIsHovered] = useState(false);
        const baseStyles = getPillStyles();
        const currentStyles = isHovered ? getHoverStyles(baseStyles) : baseStyles;

        return (
          <a
            key={index}
            href="#"
            onClick={(e) => e.preventDefault()}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              ...currentStyles,
              ...(c.layout === 'grid' ? { justifyContent: 'center', textAlign: 'center' } : {}),
            }}
          >
            {c.showSearchIcon && <SearchIconSvg size={size.iconSize} />}
            <span>{term}</span>
          </a>
        );
      };

      return (
        <Box
          className={(c.customCssClass as string) || undefined}
          style={{
            backgroundColor,
            padding: isGalleryPreview
              ? '12px'
              : c.containerPadding === 'custom' && c.customPadding
              ? (c.customPadding as string)
              : c.containerPadding === 'none'
              ? '0'
              : c.containerPadding === 'sm'
              ? '0.5rem'
              : c.containerPadding === 'lg'
              ? '1.5rem'
              : '1rem',
            borderRadius: '8px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {c.showHeading && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: isGalleryPreview ? '10px' : '14px',
              }}
            >
              {c.headingStyle === 'with-icon' && <SearchIconSvg size={isGalleryPreview ? 14 : 18} />}
              <Typography component="h4" style={getHeadingStyles()}>
                {(c.headingText as string) || 'Related Searches'}
              </Typography>
              {/* View More inline with heading */}
              {isExpandable && hasMorePills && !isGalleryPreview && (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleExpand('related-searches');
                  }}
                  style={{
                    marginLeft: 'auto',
                    fontSize: '0.8rem',
                    color: pillColor,
                    textDecoration: 'none',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {isExpanded ? '← Show Less' : `${viewMoreText} (${maxPills - pillsPerRow} more)`}
                </a>
              )}
            </Box>
          )}
          <Box style={getContainerStyles()}>
            {SAMPLE_SEARCHES.slice(0, visiblePills).map((term, index) => (
              <Pill key={index} term={term} index={index} />
            ))}
          </Box>
        </Box>
      );
    }

    default:
      return (
        <Box p={2}>
          <Typography variant="body2" color="textSecondary">
            Template preview not available
          </Typography>
        </Box>
      );
  }
};

export default TemplatePreviewRenderer;
